import type {
  IInstrumentRecommendation,
  IInstrumentRecommendationCard,
  IInstrumentRecommendationsPanel,
  InstrumentRecommendationBadge,
} from '@brushfire/types'
import { LoisRecommendationStatus } from '@brushfire/types'

function isRecommendationsPanel(value: unknown): value is IInstrumentRecommendationsPanel {
  return (
    typeof value === 'object' &&
    value !== null &&
    'recommendations' in value &&
    Array.isArray((value as IInstrumentRecommendationsPanel).recommendations)
  )
}

function mapBrownfieldRecommendation(
  recommendation: IInstrumentRecommendation,
): IInstrumentRecommendationCard {
  return {
    id: recommendation._id,
    text: recommendation.claim,
    category: 'investigate_further',
    status:
      recommendation.status === LoisRecommendationStatus.Confirmed
        ? 'CONFIRMED'
        : ('PARTIAL' as InstrumentRecommendationBadge),
    sourceInsightIds: recommendation.evidenceLinks ?? [],
    notes: (recommendation.notes ?? []).map((note) => ({
      _id: note._id,
      authorId: note.authorId,
      authorName: note.authorName,
      authorRole: note.authorRole,
      text: note.text,
      createdAt:
        note.createdAt instanceof Date ? note.createdAt.toISOString() : String(note.createdAt),
      updatedAt:
        note.updatedAt instanceof Date ? note.updatedAt.toISOString() : String(note.updatedAt),
    })),
    lastEvaluatedAt:
      recommendation.lastEvaluatedAt instanceof Date
        ? recommendation.lastEvaluatedAt.toISOString()
        : String(recommendation.lastEvaluatedAt),
  }
}

/** Normalize brownfield single recommendation or BFP-654 recommendations panel payload. */
export function normalizeInstrumentRecommendationsPanel(
  data: IInstrumentRecommendationsPanel | IInstrumentRecommendation | null | undefined,
  fallback?: {
    recommendationGenerationInProgress?: boolean
    recommendationGenerationFailed?: boolean
    hasInsights?: boolean
  },
): IInstrumentRecommendationsPanel | null {
  if (!data) {
    if (!fallback?.hasInsights) return null
    return {
      recommendations: [],
      recommendationGenerationInProgress: fallback.recommendationGenerationInProgress === true,
      recommendationGenerationFailed: fallback.recommendationGenerationFailed === true,
      blocked: false,
    }
  }

  if (isRecommendationsPanel(data)) {
    return {
      ...data,
      recommendations: data.recommendations.slice(0, 3),
    }
  }

  return {
    recommendations: [mapBrownfieldRecommendation(data)],
    recommendationGenerationInProgress: fallback?.recommendationGenerationInProgress === true,
    recommendationGenerationFailed: fallback?.recommendationGenerationFailed === true,
    blocked: false,
  }
}
